import * as React from 'react';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Typography from '@mui/material/Typography';
import {createTheme, ThemeProvider} from '@mui/material/styles';
import {useSnackbar} from 'notistack';
import {useState, useEffect} from 'react';
import {Link} from 'react-router';
import {Image, InfoRounded, Language, AndroidOutlined, GetApp} from '@mui/icons-material';

import {useApplicationContext} from '../../ApplicationContext';
import {useTranslation} from 'react-i18next';
import {AppStoreInfoDto} from '../../apiClient/Api';
import {IconButton, Menu, MenuItem, Alert, AlertTitle, Dialog, DialogTitle, DialogContent, DialogActions} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {useMediaQuery} from '@mui/material';
import {Copyright} from "../../Components/Copyright";
import LanguageButton from "../../Components/LanguageButton";
import { SupportIcon } from '../../SupportIcon';

/**
 * Detects if the current device is an Android device
 * Checks multiple indicators for better reliability
 * @returns boolean indicating if the device is Android
 */
const isAndroidDevice = (): boolean => {
    const userAgent = navigator.userAgent.toLowerCase();
    const platform = navigator.platform?.toLowerCase() || '';
    
    // Check multiple Android indicators
    const hasAndroidInUA = userAgent.includes('android');
    const hasLinuxInPlatform = platform.includes('linux') && !platform.includes('mac');
    const hasMobileKeyword = userAgent.includes('mobile') || userAgent.includes('wv');
    
    // Additional Android-specific checks
    const hasAndroidKeywords = 
        userAgent.includes('android') ||
        userAgent.includes('linux; u;') ||
        (userAgent.includes('linux') && (userAgent.includes('mobile') || userAgent.includes('android')));
    return hasAndroidInUA || (hasLinuxInPlatform && hasMobileKeyword) || hasAndroidKeywords;
};

export function SignInSide() {
    const context = useApplicationContext();
    const {enqueueSnackbar} = useSnackbar();
    const [email, setEmail] = useState('');
    const [isAndroid, setIsAndroid] = useState(false);
    const [showAndroidDialog, setShowAndroidDialog] = useState(false);
    const [apkInfo, setApkInfo] = useState<AppStoreInfoDto>({available: false});

    const {t, i18n} = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isVerySmallScreen = useMediaQuery('(max-width:640px)');
    // Verstecke Hintergrundbild bei Displays unter 1200px (7-9 Zoll Tablets)
    const isSmallDevice = useMediaQuery('(max-width:1199px)');
    const isLandscape = useMediaQuery('(orientation: landscape) and (max-height: 600px)');
    // Verschiedene Bildschirmhöhen für dynamische Logo-Größe
    const isVeryLowHeight = useMediaQuery('(max-height: 650px)');
    const isLowHeight = useMediaQuery('(max-height: 750px)');
    const isMediumHeight = useMediaQuery('(max-height: 850px)');

    useEffect(() => {
        const isAndroid = isAndroidDevice();
        setIsAndroid(isAndroid);
        
        // Prüfe ob User den Dialog bereits gesehen/abgelehnt hat
        const hasSeenDialogPermanent = localStorage.getItem('androidAppDialogDismissedPermanent');
        const hasSeenDialogSession = sessionStorage.getItem('androidAppDialogDismissedSession');
        
        // Prüfe ob APK verfügbar ist und hole Version-Info
        if (isAndroid && !hasSeenDialogPermanent && !hasSeenDialogSession) {
            context.api.appStore.appStoreGetAppStoreInfo()
                .then(response => {
                    setApkInfo(response.data);
                    if (response.data.available) {
                        setShowAndroidDialog(true);
                    }
                })
                .catch(error => {
                    console.error('Failed to check AppStore availability:', error);
                    setApkInfo({available: false});
                });
        }
    }, []);

    /**
     * Handles the download of the Android AppStore APK file
     */
    const handleDownloadAPK = () => {
        context.api.appStore.appStoreDownloadAppStore()
            .then(response => {
                // Erstelle Download-Link
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'reichert-appstore.apk');
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                
                enqueueSnackbar(t('signin.downloadStarted'), {
                    variant: 'success',
                });
                
                // Nach erfolgreichem Download Dialog nicht mehr anzeigen
                localStorage.setItem('androidAppDialogDismissedPermanent', 'true');
            })
            .catch(error => {
                console.error('Failed to download AppStore:', error);
                enqueueSnackbar(t('signin.downloadFailed'), {
                    variant: 'error',
                });
            });
        
        setShowAndroidDialog(false);
    };

    /**
     * Handles dismissing the Android dialog
     */
    const handleDismissAndroidDialog = () => {
        setShowAndroidDialog(false);
        sessionStorage.setItem('androidAppDialogDismissedSession', 'true');
    };

    /**
     * Handles never showing the Android dialog again
     */
    const handleNeverShowAgain = () => {
        setShowAndroidDialog(false);
        localStorage.setItem('androidAppDialogDismissedPermanent', 'true');
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        var cookieAccept = data.get('cookies') as string;
        if (cookieAccept !== 'cookiesAccepted') {
            enqueueSnackbar(t('signin.cookiesWarning'), {
                variant: 'warning',
            });
            return;
        }

        var formData = {
            email: data.get('email') as string,
            password: data.get('password') as string,
            remember: data.get('remember') === 'rememberMe',
        };
        context.signIn(formData.email, formData.password, formData.remember);
    };

    return (
        <Grid container component="main" sx={{height: '100vh'}}>
            <CssBaseline/>
            {/* Hintergrundbild - nur auf großen Displays (>1200px) */}
            {!isSmallDevice && (
                <Grid
                    item
                    xs={false}
                    sm={4}
                    md={7}
                    sx={{
                        backgroundImage: 'url(/login_background.jpeg)',
                        backgroundRepeat: 'no-repeat',
                        backgroundColor: (theme) =>
                            theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.grey[900],
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                    }}
                />
            )}
            <Grid 
                item 
                xs={12} 
                sm={isSmallDevice ? 12 : 8} 
                md={isSmallDevice ? 12 : 5} 
                component={Paper} 
                elevation={6} 
                square
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    overflowY: 'auto',
                }}
            >
                <Box
                    sx={{
                        my: isLandscape ? 1 : isVeryLowHeight ? 1.5 : isLowHeight ? 2 : {xs: 2, sm: 8},
                        mx: {xs: 1.5, sm: 4},
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        flex: 1,
                        maxWidth: '100%',
                        minHeight: 0, // Ermöglicht besseres Scrolling
                    }}
                >
                    {/* Horizontales Layout für kleine Displays: Logo links, Content rechts */}
                    {isVerySmallScreen ? (
                        <Grid container spacing={2} alignItems="flex-start" sx={{ width: '100%', mb: 1 }}>
                            <Grid item xs={3} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 1 }}>
                                <SupportIcon size={isVeryLowHeight ? 60 : 80} />
                            </Grid>
                            <Grid item xs={9}>
                                <Typography 
                                    component="h1" 
                                    variant="h6"
                                    sx={{
                                        fontSize: isVeryLowHeight ? '0.95rem' : '1.1rem',
                                        fontWeight: 500,
                                    }}
                                >
                                    {t('signin.login')}
                                </Typography>
                            </Grid>
                        </Grid>
                    ) : (
                        <>
                            <SupportIcon 
                                size={
                                    isLandscape ? 80 :
                                    isVeryLowHeight ? 100 : 
                                    isLowHeight ? 120 : 
                                    isMediumHeight ? 160 : 
                                    200
                                } 
                            />
                            <Typography 
                                component="h1" 
                                variant={isMobile ? "h6" : "h5"} 
                                sx={{
                                    mt: isLandscape ? 0.5 : isVeryLowHeight ? 0.5 : 1,
                                    mb: isLandscape ? 0.5 : isVeryLowHeight ? 0.5 : 1,
                                    fontSize: isVeryLowHeight ? '1.1rem' : undefined,
                                }}
                            >
                                {t('signin.login')}
                            </Typography>
                        </>
                    )}

                    {/* Info-Box - nur auf größeren Displays anzeigen */}
                    {!isLandscape && !isVerySmallScreen && !isVeryLowHeight && (
                        <Grid 
                            container 
                            sx={{
                                justifyContent: 'center', 
                                alignItems: 'center',
                                mt: isLowHeight ? 0.5 : 1,
                                mb: isLowHeight ? 0.5 : 1,
                            }}
                        >
                            <Grid item xs={2} sm={1} sx={{display: 'flex', justifyContent: 'center'}}>
                                <Avatar
                                    sx={{
                                        color: (theme) => theme.palette.info.dark,
                                        backgroundColor: (theme) => theme.palette.info.light,
                                        width: {xs: 32, sm: 40},
                                        height: {xs: 32, sm: 40},
                                    }}
                                >
                                    <InfoRounded sx={{fontSize: {xs: 20, sm: 24}}} />
                                </Avatar>
                            </Grid>
                            <Grid item xs={10} sm={11} sx={{pl: 1}}>
                                <Typography 
                                    variant="caption" 
                                    color="info.main" 
                                    sx={{
                                        fontSize: {xs: '0.7rem', sm: '0.75rem'},
                                        lineHeight: 1.3,
                                    }}
                                >
                                    {t('signin.info')}
                                </Typography>
                            </Grid>
                        </Grid>
                    )}
                    <Box component="form" noValidate onSubmit={handleSubmit} sx={{mt: isLandscape ? 0.5 : isVeryLowHeight ? 0.5 : 1, width: '100%'}}>
                        <TextField
                            margin={isLandscape ? "dense" : "normal"}
                            required
                            fullWidth
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            label={t('signin.emailLabel')}
                            name="email"
                            autoComplete="email"
                            autoFocus
                            size={isVerySmallScreen ? "small" : "medium"}
                            InputLabelProps={{
                                style: { fontSize: isVerySmallScreen ? '0.875rem' : undefined }
                            }}
                        />
                        <TextField
                            margin={isLandscape ? "dense" : "normal"}
                            required
                            fullWidth
                            name="password"
                            label={t('signin.passwordLabel')}
                            type="password"
                            id="password"
                            autoComplete="current-password"
                            size={isVerySmallScreen ? "small" : "medium"}
                            InputLabelProps={{
                                style: { fontSize: isVerySmallScreen ? '0.875rem' : undefined }
                            }}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox 
                                    value="rememberMe" 
                                    color="primary" 
                                    id="remember" 
                                    name="remember"
                                    size={isVerySmallScreen ? "small" : "medium"}
                                />
                            }
                            label={
                                <Typography sx={{ fontSize: isVerySmallScreen ? '0.8rem' : '0.875rem' }}>
                                    {t('signin.rememberMe')}
                                </Typography>
                            }
                            sx={{ mt: isLandscape ? 0 : 0.5 }}
                        />
                        <br/>
                        <FormControlLabel
                            control={
                                <Checkbox 
                                    value="cookiesAccepted" 
                                    color="primary" 
                                    id="cookies"
                                    name="cookies"
                                    size={isVerySmallScreen ? "small" : "medium"}
                                />
                            }
                            label={
                                <Typography sx={{ fontSize: isVerySmallScreen ? '0.8rem' : '0.875rem' }}>
                                    {t('signin.cookiesAgreement')}
                                </Typography>
                            }
                        />

                        <Grid container spacing={isVerySmallScreen || isVeryLowHeight ? 1 : 2} sx={{ mt: isLandscape ? 0.5 : isVeryLowHeight ? 1 : 2.5 }}>
                            <Grid item xs={12} sm={6}>
                                <Button 
                                    type="submit" 
                                    fullWidth 
                                    variant="contained" 
                                    sx={{
                                        py: isVerySmallScreen || isVeryLowHeight ? 0.75 : 1,
                                        fontSize: isVerySmallScreen || isVeryLowHeight ? '0.8rem' : '0.875rem',
                                    }}
                                >
                                    {t('signin.loginButton')}
                                </Button>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    component={Link}
                                    to={'/support'}
                                    color={'secondary'}
                                    sx={{
                                        py: isVerySmallScreen || isVeryLowHeight ? 0.75 : 1,
                                        fontSize: isVerySmallScreen || isVeryLowHeight ? '0.8rem' : '0.875rem',
                                    }}
                                >
                                    {t('signin.needSupport')}
                                </Button>
                            </Grid>
                        </Grid>
                        <Grid container spacing={isVerySmallScreen ? 0.5 : 1}>
                            <Grid item xs={12} sm={6}>
                                <Button
                                    fullWidth={isMobile}
                                    size="small"
                                    sx={{
                                        fontSize: isVerySmallScreen ? '0.7rem' : '0.8125rem',
                                        py: isVerySmallScreen ? 0.5 : 0.75,
                                    }}
                                    onClick={() => {
                                        context.api.user.userPasswordForgotten(email).catch(context.apiError).then(() => {
                                            enqueueSnackbar("Ihre Anfrage ist eingegangen. Falls Sie einen Nutzeraccount haben bekommen Sie in kürze eine E-Mail", {variant: "warning"})
                                        })
                                    }}
                                >
                                    {t('signin.forgotPassword')}
                                </Button>
                            </Grid>
                            <Grid item xs={12} sm={6} sx={{display: 'flex', justifyContent: {xs: 'flex-start', sm: 'flex-end'}}}>
                                <Button 
                                    fullWidth={isMobile}
                                    size="small"
                                    component={Link} 
                                    to={'/SignUp'}
                                    sx={{
                                        fontSize: isVerySmallScreen ? '0.7rem' : '0.8125rem',
                                        py: isVerySmallScreen ? 0.5 : 0.75,
                                    }}
                                >
                                    {t('signin.noAccount')}
                                </Button>
                            </Grid>
                        </Grid>

                        <br/>

                        <Typography 
                            align={'center'} 
                            variant={'caption'} 
                            sx={{
                                fontSize: isVerySmallScreen ? '0.6rem' : {xs: '0.65rem', sm: '0.75rem'},
                                lineHeight: 1.3,
                                display: isLandscape ? 'none' : 'block',
                            }}
                        >
                            {t('signin.cookiesInfo')}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{mt: 'auto', pb: isLandscape ? 1 : isVeryLowHeight ? 1 : 2}}>
                    <Copyright />
                </Box>
                {/* Floating Language Change Button */}
                <LanguageButton floating />
            </Grid>

            {/* Android App Download Dialog */}
            <Dialog 
                open={showAndroidDialog} 
                onClose={handleDismissAndroidDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        mx: 2,
                        borderRadius: 2,
                    }
                }}
            >
                <DialogTitle sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    pb: 1,
                }}>
                    <AndroidOutlined color="success" sx={{ fontSize: 28 }} />
                    {t('signin.androidDetected')}
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: '0.95rem', mb: apkInfo.version ? 1 : 2 }}>
                        {t('signin.androidDownloadInfo')}
                    </Typography>
                    {apkInfo.version && (
                        <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                            {t('signin.version')}: {apkInfo.version}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ 
                    px: { xs: 2, sm: 3 }, 
                    pb: 2, 
                    pt: 1,
                    gap: 1, 
                    flexDirection: 'column',
                }}>
                    <Button
                        onClick={handleDownloadAPK}
                        variant="contained"
                        color="success"
                        startIcon={<GetApp />}
                        fullWidth
                        disabled={!apkInfo.available}
                        sx={{ mb: 0.5 }}
                    >
                        {t('signin.download')}
                    </Button>
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 1, 
                        width: '100%',
                        flexDirection: { xs: 'column', sm: 'row' }
                    }}>
                        <Button
                            onClick={handleDismissAndroidDialog}
                            color="inherit"
                            fullWidth
                            size="small"
                        >
                            {t('signin.notNow')}
                        </Button>
                        <Button
                            onClick={handleNeverShowAgain}
                            color="inherit"
                            fullWidth
                            size="small"
                        >
                            {t('signin.neverShowAgain')}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
        </Grid>
    );
}
